import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
// hooks
import { useAuthContext } from 'src/auth/useAuthContext';
//
import { usePreferencesUIState } from 'src/state/ui/usePreferencesUIState';
import { defaultSettings, defaultThemeColorPreset } from './config-setting';
import { defaultPreset, getPresets, presetsOption } from './presets';
import {
  ThemeColorPresetsValue,
  ThemeContrastValue,
  ThemeModeValue,
  ThemeSettingsContextProps,
  ThemeSettingsValueProps,
} from './types';

const initialState: ThemeSettingsContextProps = {
  ...defaultSettings,
  // Mode
  onToggleMode: () => {},
  onChangeMode: () => {},
  // Direction
  onToggleDirection: () => {},
  onChangeDirection: () => {},
  // Contrast
  onToggleContrast: () => {},
  onChangeContrast: () => {},
  // Color
  onChangeColorPresets: () => {},
  themeColorPreset: defaultThemeColorPreset,
  presetsColor: defaultPreset(defaultSettings.themeMode) as any,
  presetsOption: [],
  headerColor: { color: defaultThemeColorPreset, level: 500 },
  // Stretch
  onToggleStretch: () => {},
  // Reset
  onResetSetting: () => {},

  // Open
  open: false,
  setOpen: () => {},
};

export const ThemeSettingsContext = createContext(initialState);

export const useThemeSettingsContext = () => {
  const context = useContext(ThemeSettingsContext);

  if (!context)
    throw new Error(
      'useThemeSettingsContext must be use inside SettingsProvider',
    );

  return context;
};

type ThemeSettingsProviderProps = {
  children: React.ReactNode;
};

export function ThemeSettingsProvider({
  children,
}: ThemeSettingsProviderProps) {
  const [themeColorPreset, setThemeColorPreset] =
    useState<ThemeColorPresetsValue>(defaultThemeColorPreset);

  const { user, updateSettings } = useAuthContext();

  const [open, setOpen] = useState(false);

  const { preferenceState, setPreferenceState } = usePreferencesUIState();

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (user?.settings && !preferenceState) {
      setPreferenceState(user?.settings);
    }
  }, [user?.settings, preferenceState]);

  const settings = useMemo(
    () => preferenceState || defaultSettings,
    [preferenceState],
  );

  const headerColor = useMemo(
    () => ({
      color: themeColorPreset,
      level: settings.themeMode === 'light' ? 500 : 300,
    }),
    [themeColorPreset, settings.themeMode],
  );

  const setSettingsState = useCallback(
    (newSettings: ThemeSettingsValueProps) => {
      setPreferenceState(newSettings);
      updateSettings(newSettings);
    },
    [setPreferenceState, updateSettings],
  );

  // Mode
  const onToggleMode = useCallback(() => {
    const themeMode = settings.themeMode === 'light' ? 'dark' : 'light';
    setSettingsState({ ...settings, themeMode });
  }, [settings, setSettingsState]);

  const onChangeMode = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const themeMode = event.target.value as ThemeModeValue;
      setSettingsState({ ...settings, themeMode });
    },
    [settings, setSettingsState],
  );

  // Contrast
  const onToggleContrast = useCallback(() => {
    const themeContrast =
      settings.themeContrast === 'default' ? 'bold' : 'default';
    setSettingsState({ ...settings, themeContrast });
  }, [settings, setSettingsState]);

  const onChangeContrast = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const themeContrast = event.target.value as ThemeContrastValue;
      setSettingsState({ ...settings, themeContrast });
    },
    [settings, setSettingsState],
  );

  // Color
  const onChangeColorPresets = useCallback(
    (color: ThemeColorPresetsValue) => {
      if (themeColorPreset === color) return;
      setThemeColorPreset(color);
    },
    [themeColorPreset],
  );

  // Reset
  const onResetSetting = useCallback(() => {
    setPreferenceState(defaultSettings);
    updateSettings(defaultSettings);
  }, [setPreferenceState, updateSettings]);

  const memoizedValue = useMemo(
    () => ({
      ...settings,
      // Mode
      onToggleMode,
      onChangeMode,
      // Contrast
      onChangeContrast,
      onToggleContrast,
      // Stretch
      // Color
      onChangeColorPresets,
      themeColorPreset,
      presetsOption: presetsOption(settings.themeMode),
      presetsColor: getPresets(themeColorPreset, settings.themeMode),
      // Reset
      onResetSetting,
      // Open
      open,
      setOpen,
      headerColor,
    }),
    [
      settings,
      themeColorPreset,
      onToggleMode,
      onChangeMode,
      onChangeContrast,
      onToggleContrast,
      onChangeColorPresets,
      onResetSetting,
      open,
      headerColor,
    ],
  ) as ThemeSettingsContextProps;

  return (
    <ThemeSettingsContext.Provider value={memoizedValue}>
      {children}
    </ThemeSettingsContext.Provider>
  );
}
